// src/components/ManabaRosterImport.jsx
// manaba の履修者名簿の取り込み【2026-09-17 授業の名簿から切り出し】
//
//   classId あり … その授業の画面から取り込む。年度・学期・担当はその授業に合わせる
//   classId なし … すべての授業から取り込む。年度・学期（教務なら担当教員も）を画面で選ぶ
//
//   どちらも「中身を確認する（dryRun）→ コースごとに行き先を選ぶ → この内容で取り込む」の2段。
//   1本のファイルに複数コースが入っているのが普通なので、行き先はコース単位で決める。
import React, { useEffect, useRef, useState } from 'react';
import RosterPreviewStudents from './RosterPreviewStudents';

export default function ManabaRosterImport({
  classId = null, cls = null, classes = [], token, setErr, setMsg, onImported,
  isStaff = false, defaultYear = '',
  // 画面のどこかに落とされたファイル（親が受けて渡す）【2026-09-17】
  incomingFile = null, onIncomingTaken, onKyomuDetected }) {
  // 授業を開いているなら、その授業の年度・学期に固定する
  const fixedYear = !!(classId && cls);
  const [years, setYears]         = useState([]);
  const [terms, setTerms]         = useState([]);
  const [year, setYear]           = useState(/^\d{4}$/.test(String(defaultYear)) ? String(defaultYear) : '');
  const [termId, setTermId]       = useState('');
  const [teachers, setTeachers]   = useState([]);
  const [teacherId, setTeacherId] = useState('');

  useEffect(() => {
    if (fixedYear) return;
    const h = { Authorization: 'Bearer ' + token() };
    fetch('/api/terms', { headers: h })
      .then(r => r.json())
      .then(d => {
        if (!d.success) return;
        setTerms(d.terms || []);
        const ys = new Set((d.yearOptions || []).map(Number));
        if (/^\d{4}$/.test(String(defaultYear))) ys.add(Number(defaultYear));
        setYears([...ys].sort((a, b) => b - a));
        setYear(y => y || String(d.defaults?.academicYear ?? ''));
        setTermId(t => t || (d.defaults?.termId ? String(d.defaults.termId) : ''));
      })
      .catch(() => {});
    if (isStaff) {
      fetch('/api/csv-import/teachers', { headers: h })
        .then(r => r.json())
        .then(d => { if (d.success) setTeachers(d.teachers || []); })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedYear, isStaff]);

  // 画面上部で年度を切り替えたら、取り込みの年度も合わせる
  useEffect(() => {
    if (!fixedYear && /^\d{4}$/.test(String(defaultYear))) setYear(String(defaultYear));
  }, [defaultYear, fixedYear]);

  const [rosterFile, setRosterFile]     = useState(null);
  const [rosterBusy, setRosterBusy]     = useState(false);
  const [rosterResult, setRosterResult] = useState(null);
  // 取り込み前の確認【2026-09-11】
  //   この取り込みはファイルのコース名を無視して全行をこの授業に入れる。
  //   複数コース分のファイルを落とすと、数百人がまとめて入ってしまう。
  //   先に dryRun で同じ処理を流して巻き戻し、件数を見てから確定する。
  const [rosterPreview, setRosterPreview] = useState(null);
  const [rosterDrag, setRosterDrag] = useState(false);
  // コースごとの行き先【2026-09-11】
  //   { "コース名": "12" | "new" | "skip" }
  //   「開いている授業に全部入れる」はやめた。1本のファイルに複数コースが
  //   入っているのが普通で、全部まとめて入れて良い場面がほとんど無い。
  //   まず中身を読んで、コースごとに行き先を決める。
  const [rosterMap, setRosterMap] = useState({});
  const rosterInputRef = useRef(null);
  // 落とされたファイルが manaba か教務システムかを見てもらっている間【2026-09-18】
  const [sniffing, setSniffing] = useState(false);

  /**
   * 落とされた／選ばれたファイルを1つ受け取る【2026-09-11】
   *
   *   【2026-09-18】名簿の入口はこの1つにまとめた。
   *   manaba か教務システムの受講者一覧かは拡張子では決まらないので、
   *   まずサーバーに見出しを見てもらい、受講者一覧なら親へ回す。
   *   ファイルは手元に持ったまま渡すので、上げ直しにはならない。
   */
  const takeRosterFile = async (f) => {
    if (!f) return;
    if (!/\.(csv|tsv|xls|xlsx|xlsm|ods)$/i.test(f.name)) {
      setErr(f.name + '： Excel（.xls / .xlsx）・.ods・CSV のどれかを落としてください');
      return;
    }
    setErr('');

    if (onKyomuDetected) {
      setSniffing(true);
      try {
        const fd = new FormData();
        fd.append('file', f);
        const r = await fetch('/api/csv-import/kyomu/preview', {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + (token ? token() : localStorage.getItem('token')) },
          body: fd,
        });
        const d = await r.json();
        if (d.success) { onKyomuDetected(f); return; }     // 受講者一覧だった
        // LOOKS_LIKE_MANABA 以外（壊れている・読めない）はそのまま出す
        if (d.code && d.code !== 'LOOKS_LIKE_MANABA') { setErr(d.message); return; }
      } catch (e) {
        // 判別に失敗しても manaba として進めてよい（従来どおりの動き）
      } finally {
        setSniffing(false);
      }
    }

    setRosterFile(f);
    setRosterPreview(null);
    setRosterMap({});
    setAllowNames([]);
    setOpenCourse(null);
  };
  // 学籍番号が同じで名前が違う学生がいても取り込むコース名【2026-10-05】
  const [allowNames, setAllowNames] = useState([]);
  const [openCourse, setOpenCourse] = useState(null);

  // 親から渡されたファイルを受け取る。受け取ったら親に知らせて空にしてもらう
  //   ドロップ枠の onDrop は preventDefault するので、画面全体の受け口とは二重にならない
  useEffect(() => {
    if (!incomingFile) return;
    takeRosterFile(incomingFile);
    if (onIncomingTaken) onIncomingTaken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingFile]);

  // 年度・学期・担当を変えたら、確認結果は作り直す（行き先の授業が変わるため）
  const resetFor = (fn) => { fn(); setRosterPreview(null); setRosterMap({}); setOpenCourse(null); };

  /** 名簿CSVを送る。dryRun なら件数だけ見て、DBには何も残さない */
  const postRoster = async (dryRun, map) => {
    const fd = new FormData();
    fd.append('file', rosterFile);
    // classId は送らない。送るとコース名を無視して全行が1つの授業に入る。
    // 行き先はコースごとに classMap で指定する。
    if (map && Object.keys(map).length > 0) fd.append('classMap', JSON.stringify(map));
    if (dryRun) fd.append('dryRun', '1');
    // 学籍番号が同じで名前が違っても取り込むコース（画面で確かめたもの）【2026-10-05】
    if (allowNames.length > 0) fd.append('allowNameMismatch', JSON.stringify(allowNames));
    // 年度・学期【2026-09-17】
    //   授業を開いているときはその授業に合わせる。送らないとサーバーは「今の年度」で
    //   探す・作るので、去年の授業から取り込んだのに今年の授業ができてしまう。
    //   すべての授業のときは、画面で選んだ年度・学期を使う。
    if (fixedYear) {
      if (cls.academic_year) fd.append('academicYear', String(cls.academic_year));
      if (cls.term_id) fd.append('termId', String(cls.term_id));
    } else {
      if (year) fd.append('academicYear', year);
      if (termId) fd.append('termId', termId);
    }
    // 教務・開発者は「誰の授業か」が必須（教員本人なら不要）
    const owner = cls && cls.teacher_id ? String(cls.teacher_id) : teacherId;
    if (isStaff && owner) fd.append('teacherId', owner);
    const res = await fetch('/api/csv-import/manaba', {
      method: 'POST', headers: { Authorization: 'Bearer ' + token() }, body: fd
    });
    const d = await res.json();
    if (!res.ok || !d.success) throw new Error(d.message || '取り込みに失敗しました');
    return d.result;
  };

  // 1段目: 何が入るのかを確かめる（保存しない）
  //   割り当てを渡さずに一度読むと、サーバーがコース名で探した結果が
  //   byClass に入って返る。それを初期値にして、画面で直せるようにする。
  const checkRoster = async (e, map) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!rosterFile) return;
    if (isStaff && !(cls && cls.teacher_id) && !teacherId) { setErr('担当教員を選んでください'); return; }
    setRosterBusy(true); setErr(''); setMsg(''); setRosterResult(null);
    try {
      const result = await postRoster(true, map || rosterMap);
      setRosterPreview(result);
      // 初回だけ、サーバーが決めた行き先を初期値として取り込む
      if (!map && Object.keys(rosterMap).length === 0 && result.byClass) {
        const init = {};
        for (const b of result.byClass) {
          init[b.className] = b.isNew ? 'new' : String(b.classId);
        }
        setRosterMap(init);
      }
    } catch (e) { setErr(e.message); }
    finally     { setRosterBusy(false); }
  };

  // いま開いている授業【2026-09-14】
  // 行き先のプルダウンに「どれが今の授業か」が出ていないと、
  // 結局どれを選べばいいのか分からない。先頭に固定で出す。
  const currentClass = classId ? classes.find(c => String(c.id) === String(classId)) : null;
  // 行き先の候補。教務が代理で入れるときは、選んだ先生の授業に絞る
  const otherClasses = classes.filter(c => String(c.id) !== String(classId)
    && (!isStaff || fixedYear || !teacherId || String(c.teacher_id) === String(teacherId)));

  const setRosterTarget = (courseName, value) => {
    setRosterMap(m => ({ ...m, [courseName]: value }));
  };

  // 2段目: 確定
  const importRoster = async () => {
    if (!rosterFile) return;
    // 同じ名前のファイルを前に取り込んでいたら、更新してよいか聞く（先生ごと）【2026-10-01】
    const same = (rosterPreview && rosterPreview.sameFileImports) || [];
    if (same.length > 0) {
      const last = same[0];
      const when = last.importedAt ? new Date(last.importedAt).toLocaleString('ja-JP') : '';
      if (!window.confirm(
        `「${rosterPreview.fileName || rosterFile.name}」は ${when} に取り込み済みです（${last.enrolledCount ?? '?'}人）。\n` +
        'この内容で更新しますか？（名簿に新しい学生が足され、前からいる学生はそのまま残ります）'
      )) return;
    }
    setRosterBusy(true); setErr(''); setMsg('');
    try {
      const result = await postRoster(false, rosterMap);
      setRosterResult(result);
      setRosterPreview(null);
      setRosterMap({});
      setMsg(`取り込みました（授業 ${result.classesCreated || 0} 件を新規作成`
           + `／${result.classesReused || 0} 件は既存）。`
           + (classId ? 'いま開いている授業以外にも入っている場合があります' : ''));
      setRosterFile(null);
      if (onImported) onImported(result);
    } catch (e) { setErr(e.message); }
    finally     { setRosterBusy(false); }
  };

  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.75rem 1rem' }}>
      <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.3rem' }}>
        manabaの名簿から取り込む
        {!classId && (
          <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#6b7280', marginLeft: '0.5rem' }}>
            （すべての授業が対象。コースごとに行き先を選べます）
          </span>
        )}
        {currentClass && (
          <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#6b7280', marginLeft: '0.5rem' }}>
            （いま開いているのは「{currentClass.name}」）
          </span>
        )}
      </div>
      <p style={{ fontSize: '0.82rem', color: '#666', margin: '0 0 0.6rem', lineHeight: 1.7 }}>
        <strong>ファイル内のコース名ごとに、取り込み先の授業を選べます。</strong>
        まず内容を確認してから、コースごとに「既存の授業」「新しく授業を作る」「取り込まない」を指定して取り込みます。
        複数コース分のファイルをまとめて入れても、コース単位で振り分けられます。
        アプリのアカウントが無い学生は仮アカウント（アカウント未作成）で作られ、本人がサインアップした時点で自動的に引き継がれます。
      </p>
      {/* 年度・学期・担当（すべての授業から取り込むとき）【2026-09-17】 */}
      {!fixedYear && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>年度</label>
          <select value={year} onChange={e => resetFor(() => setYear(e.target.value))}>
            {years.length === 0 && <option value="">（読み込み中）</option>}
            {years.map(y => <option key={y} value={String(y)}>{y}年度</option>)}
          </select>
          <select value={termId} onChange={e => resetFor(() => setTermId(e.target.value))}>
            {terms.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
          </select>
          {isStaff && (
            <>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, marginLeft: '0.5rem' }}>担当教員</label>
              <select value={teacherId} onChange={e => resetFor(() => setTeacherId(e.target.value))}>
                <option value="">— 選んでください —</option>
                {teachers.map(t => <option key={t.id} value={String(t.id)}>{t.name}（{t.student_id}）</option>)}
              </select>
            </>
          )}
          <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>
            manaba のファイルには年度が入っていないので、ここで選んだ年度の授業として入ります
          </span>
        </div>
      )}
      <form onSubmit={checkRoster}>
        {/* ドラッグ＆ドロップ【2026-09-11】
            ファイル選択ボタンだけだと、エクスプローラから持ってきた
            ファイルをいちいち選び直すことになる。落とすだけで済むようにする。 */}
        <div
          onClick={() => rosterInputRef.current && rosterInputRef.current.click()}
          onDragEnter={e => { e.preventDefault(); setRosterDrag(true); }}
          onDragOver={e => { e.preventDefault(); setRosterDrag(true); }}
          onDragLeave={e => { e.preventDefault(); setRosterDrag(false); }}
          onDrop={e => {
            e.preventDefault(); setRosterDrag(false);
            takeRosterFile((e.dataTransfer.files || [])[0]);
          }}
          style={{
            border: '2px dashed ' + (rosterDrag ? '#2563eb' : '#cbd5e1'),
            background: rosterDrag ? '#eff6ff' : '#fff',
            borderRadius: 10, padding: '0.9rem 1rem', textAlign: 'center',
            cursor: 'pointer', transition: 'background .15s, border-color .15s',
          }}
        >
          {sniffing ? (
            <div style={{ color: '#475569', fontWeight: 600 }}>ファイルの種類を確かめています…</div>
          ) : rosterFile ? (
            <div>
              <strong style={{ color: '#334155' }}>{rosterFile.name}</strong>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.2rem' }}>
                別のファイルに変えるには、ここに落とすかクリックしてください
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontWeight: 600, color: '#334155' }}>
                名簿ファイルをここにドロップ
              </div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.2rem' }}>
                manaba の履修者一覧でも、教務システムの受講者一覧でも読めます（.xls / .xlsx / .csv）
              </div>
            </div>
          )}
          <input ref={rosterInputRef} type="file" accept=".csv,.tsv,.xls,.xlsx,.xlsm,.ods"
                 key={rosterFile ? 'f' : 'e'} style={{ display: 'none' }}
                 onChange={e => { takeRosterFile(e.target.files[0]); e.target.value = ''; }} />
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
                      flexWrap: 'wrap', marginTop: '0.6rem' }}>
          <button className="btn-primary" type="submit" disabled={rosterBusy || !rosterFile}>
            {rosterBusy ? '確認中…' : '中身を確認する'}
          </button>
          {rosterFile && !rosterBusy && (
            <button type="button" className="btn-secondary"
                    onClick={() => { setRosterFile(null); setRosterPreview(null); }}>
              取り消す
            </button>
          )}
        </div>
      </form>

      {/* 取り込み前の確認【2026-09-11】 */}
      {rosterPreview && (
        <div style={{ marginTop: '0.8rem', border: '1px solid #fcd34d', background: '#fffbeb',
                      borderRadius: 10, padding: '0.8rem 1rem' }}>
          <div style={{ fontWeight: 700, color: '#92400e', fontSize: '0.92rem' }}>
            コースごとに行き先を決めてください
          </div>
          <ul style={{ margin: '0.45rem 0 0', paddingLeft: '1.2rem',
                       fontSize: '0.86rem', color: '#4b5563', lineHeight: 1.8 }}>
            <li>ファイルの行数：<strong>{rosterPreview.totalRecords}</strong> 行</li>
            <li>履修登録：<strong>{rosterPreview.enrolled}</strong> 人</li>
            <li>新しく作られる仮アカウント：<strong>{rosterPreview.usersCreated}</strong> 人</li>
            {rosterPreview.autoMerged > 0 && (
              <li>既存アカウントへの統合：{rosterPreview.autoMerged} 件</li>
            )}
            {rosterPreview.errorCount > 0 && (
              <li style={{ color: '#dc2626' }}>エラー：{rosterPreview.errorCount} 行</li>
            )}
          </ul>

          {/* コースごとの行き先【2026-09-11】
              1本のファイルに複数コースが入っているのが普通なので、
              コース単位で行き先を決める。ここで選んだものが classMap として
              サーバーに渡り、コース名の一致に依存しなくなる。 */}
          {rosterPreview.sameFileImports && rosterPreview.sameFileImports.length > 0 && (
            <div style={{ fontSize: '0.85rem', color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d',
                          borderRadius: 6, padding: '0.4rem 0.6rem', marginTop: '0.4rem' }}>
              ⚠ 同じ名前のファイル「{rosterPreview.fileName}」を
              {' '}{new Date(rosterPreview.sameFileImports[0].importedAt).toLocaleString('ja-JP')} に取り込み済みです
              （{rosterPreview.sameFileImports[0].enrolledCount}人）。取り込むと、その名簿を更新します。
            </div>
          )}
          {rosterPreview.migrationWarning && (
            <div style={{ fontSize: '0.8rem', color: '#b45309', marginTop: '0.4rem' }}>
              ⚠ {rosterPreview.migrationWarning}
            </div>
          )}
          {rosterPreview.byClass && rosterPreview.byClass.length > 0 && (
            <div className="table-wrap" style={{ marginTop: '0.6rem' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ファイルのコース名</th>
                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>人数</th>
                    <th style={{ minWidth: 200 }}>取り込み先</th>
                  </tr>
                </thead>
                <tbody>
                  {rosterPreview.byClass.map(b => {
                    const cur = rosterMap[b.className];
                    const isOpen = openCourse === b.className;
                    return (
                      <React.Fragment key={b.className}>
                      <tr>
                        <td style={{ maxWidth: 240, overflow: 'hidden',
                                     textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={b.className}>
                          {b.className}
                          {cur === 'new' && b.otherYears && b.otherYears.length > 0 && (
                            <div style={{ fontSize: '0.75rem', color: '#b91c1c', fontWeight: 700, whiteSpace: 'normal' }}>
                              ⚠ 同じ名前の授業が {b.otherYears.join('、')} にあります。年度は合っていますか？
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {b.enrolled}
                          {b.students && b.students.length > 0 && (
                            <button type="button" className="btn-secondary"
                                    onClick={() => setOpenCourse(isOpen ? null : b.className)}
                                    style={{ marginLeft: '0.4rem', fontSize: '0.75rem', padding: '0.05rem 0.45rem' }}
                                    title="このコースで取り込まれる学生を一覧で見る">
                              一覧 {isOpen ? '▲' : '▼'}
                            </button>
                          )}
                        </td>
                        <td>
                          <select value={cur === undefined ? '' : cur}
                                  disabled={rosterBusy}
                                  onChange={e => setRosterTarget(b.className, e.target.value)}
                                  style={{ maxWidth: '13rem' }}>
                            <option value="new">＋ 新しく授業を作る（{b.className}）</option>
                            {/* まとめ済みの授業（選択肢の一覧には出していない）に当たったとき【2026-09-17】 */}
                            {b.linkedToName && b.classId && !classes.some(c => String(c.id) === String(b.classId)) && (
                              <option value={String(b.classId)}>
                                {b.className} →「{b.linkedToName}」にまとめ済み
                              </option>
                            )}
                            {currentClass && (
                              <optgroup label="いま開いている授業">
                                <option value={String(currentClass.id)}>
                                  {currentClass.name}（表示中）
                                </option>
                              </optgroup>
                            )}
                            <optgroup label={currentClass ? 'ほかの授業' : '授業'}>
                              {otherClasses.map(c => (
                                <option key={c.id} value={String(c.id)}>
                                  {c.name}{c.term_name ? '（' + c.term_name + '）' : ''}
                                  {isStaff && c.teacher_name ? ' ・ ' + c.teacher_name : ''}
                                </option>
                              ))}
                            </optgroup>
                            <option value="skip">取り込まない</option>
                          </select>
                          {b.linkedToName && cur === String(b.classId) && (
                            <div style={{ fontSize: '0.78rem', color: '#1d4ed8', marginTop: 2 }}>
                              別学部の同じ授業として「{b.linkedToName}」にまとめているので、そこに入ります
                            </div>
                          )}
                          {cur === 'new' && (
                            <div style={{ fontSize: '0.78rem', color: '#b45309', marginTop: 2 }}>
                              同じ名前の授業があっても、別に作られます
                            </div>
                          )}
                          {cur === 'skip' && (
                            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2 }}>
                              この {b.enrolled} 人は入りません
                            </div>
                          )}
                          {/* 学籍番号が同じなのに名前が違う学生がいるコースは取り込まない【2026-10-05】 */}
                          {b.nameMismatchCount > 0 && cur !== 'skip' && (() => {
                            const allowed = allowNames.includes(b.className);
                            return (
                              <div style={{ fontSize: '0.78rem', marginTop: 4, padding: '0.35rem 0.5rem', borderRadius: 6,
                                            background: allowed ? '#fffbeb' : '#fef2f2',
                                            border: '1px solid ' + (allowed ? '#fcd34d' : '#fca5a5') }}>
                                <strong style={{ color: allowed ? '#92400e' : '#b91c1c' }}>
                                  ⚠ 学籍番号が同じで名前の違う学生が {b.nameMismatchCount}人。{allowed ? 'このコースも取り込みます' : 'このコースは取り込みません'}
                                </strong>
                                <div>
                                  {(b.nameMismatches || []).slice(0, 5).map(x => `${x.studentId}（名簿「${x.fileName}」／登録「${x.knownName || '—'}」）`).join('、')}
                                  {b.nameMismatchCount > 5 && ` ほか${b.nameMismatchCount - 5}人`}
                                </div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: 2 }}>
                                  <input type="checkbox" checked={allowed}
                                         onChange={e => setAllowNames(a => e.target.checked
                                           ? [...a.filter(n => n !== b.className), b.className]
                                           : a.filter(n => n !== b.className))} />
                                  学籍番号を確かめたので取り込む（押したら「この割り当てで数え直す」）
                                </label>
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={3} style={{ background: '#fff' }}>
                            <RosterPreviewStudents students={b.students} />
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: '0.6rem' }}>
            <button type="button" className="btn-secondary" disabled={rosterBusy}
                    onClick={() => checkRoster(null, rosterMap)}
                    style={{ fontSize: '0.85rem' }}>
              {rosterBusy ? '確認中…' : 'この割り当てで数え直す'}
            </button>
            <span style={{ fontSize: '0.8rem', color: '#6b7280', marginLeft: '0.5rem' }}>
              行き先を変えたら押してください
            </span>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.8rem', alignItems: 'center' }}>
            <button className="btn-primary" disabled={rosterBusy} onClick={importRoster}>
              {rosterBusy ? '取り込み中…' : 'この内容で取り込む'}
            </button>
            <button className="btn-secondary" disabled={rosterBusy}
                    onClick={() => setRosterPreview(null)}>
              やめる
            </button>
            <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>
              まだ保存されていません
            </span>
          </div>
        </div>
      )}
      {rosterResult && (
        <p style={{ margin: '0.6rem 0 0', fontSize: '0.85rem' }}>
          履修登録 {rosterResult.enrolled} 件 ・ 仮アカウント {rosterResult.usersCreated} 人
          {rosterResult.autoMerged > 0 && ' ・ 既存アカウントへ統合 ' + rosterResult.autoMerged + ' 件'}
          {rosterResult.errorCount > 0 && (
            <span style={{ color: '#dc2626' }}> ・ エラー {rosterResult.errorCount} 件</span>
          )}
        </p>
      )}
    </div>
  );
}
